import axios from 'axios';
import OnboardingIndex from '../OnboardingIndex.vue';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  FORGOT_PASSWORD_RESET,
  LINK_FORGOT_PASSWORD
} from '@/utils/system_constants/constant.js';

function is_plain_object(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function as_object(value) {
  return is_plain_object(value) ? { ...value } : {};
}

function get_value(source, key) {
  const source_object = as_object(source);
  const found_entry = Object.entries(source_object).find(([entry_key]) => entry_key === key);
  return Array.isArray(found_entry) ? found_entry[1] : undefined;
}

function as_string(value, fallback = '') {
  return value == null ? fallback : String(value);
}

function parse_api_data(payload) {
  if (typeof payload === 'string') {
    try {
      return as_object(JSON.parse(payload));
    } catch {
      return {};
    }
  }

  return as_object(payload);
}

function get_api_error_message(error, fallback_message) {
  const error_object = as_object(error);
  const response = as_object(get_value(error_object, 'response'));
  const response_data = parse_api_data(get_value(response, 'data'));
  const message = get_value(response_data, 'message');
  const data = get_value(response_data, 'data');
  const error_message = get_value(error_object, 'message');

  return (
    as_string(message || data || error_message, '') ||
    fallback_message
  );
}

const login_route = { name: 'UserLogin' };

export default {
  name: 'ResetPassword',
  components: { OnboardingIndex },
  mixins: [alert_mixin],
  props: {
    link: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      user_id: null,
      new_password: '',
      confirm_password: '',
      verification_running: false,
      link_verification_running: true,
      link_error: ''
    };
  },
  mounted() {
    this.verify_reset_link();
  },
  methods: {
    get_reset_password_validation_error() {
      if (!this.user_id) {
        return 'Reset link is invalid or expired.';
      }

      if (!this.new_password || !this.confirm_password) {
        return 'Please enter and confirm your new password.';
      }

      if (this.new_password !== this.confirm_password) {
        return 'Passwords do not match.';
      }

      return '';
    },

    get_reset_password_payload() {
      return {
        user_id: this.user_id,
        new_password: this.new_password,
        confirm_password: this.confirm_password
      };
    },

    is_reset_password_failure(response_data) {
      const status = as_string(get_value(response_data, 'status'));
      return Boolean(status && status !== 'success');
    },

    async verify_reset_link() {
      this.link_verification_running = true;
      this.link_error = '';

      try {
        const response = await axios.get(LINK_FORGOT_PASSWORD, {
          params: { link: this.link }
        });
        const response_data = parse_api_data(response.data);

        const status = as_string(get_value(response_data, 'status'));
        if (status !== 'success') {
          this.link_error = as_string(get_value(response_data, 'message'), 'Invalid or expired reset link.');
          return;
        }

        this.user_id = get_value(response_data, 'user_id');
      } catch (caught_error) {
        this.link_error = get_api_error_message(
          caught_error,
          'Invalid or expired reset link.'
        );
      } finally {
        this.link_verification_running = false;
      }
    },

    async reset_password() {
      const validation_error = this.get_reset_password_validation_error();

      if (validation_error) {
        this.show_error(validation_error);
        return;
      }

      this.verification_running = true;

      try {
        const response = await axios.post(
          FORGOT_PASSWORD_RESET,
          this.get_reset_password_payload()
        );
        const response_data = parse_api_data(response.data);

        if (this.is_reset_password_failure(response_data)) {
          this.show_error(as_string(get_value(response_data, 'message'), 'Failed to reset password.'));
          return;
        }

        this.show_success(as_string(get_value(response_data, 'message'), 'Password reset successfully.'));
        this.$router.push(login_route);
      } catch (caught_error) {
        this.show_error(get_api_error_message(caught_error, 'Failed to reset password.'));
      } finally {
        this.verification_running = false;
      }
    }
  }
};
